import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

interface AddCustomSizeDialogProps {
  onAddSize: (label: string, value: string) => Promise<{ ok: boolean; error?: string }>;
  isLoading?: boolean;
  trigger?: React.ReactNode;
}

export function AddCustomSizeDialog({
  onAddSize,
  isLoading = false,
  trigger,
}: AddCustomSizeDialogProps) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim() || !value.trim()) {
      toast.error("Preencha o rótulo e o valor do tamanho");
      return;
    }

    setSubmitting(true);
    try {
      const result = await onAddSize(label.trim(), value.trim().toUpperCase());

      if (result.ok) {
        toast.success(`Tamanho "${label}" adicionado com sucesso!`);
        setLabel("");
        setValue("");
        setOpen(false);
      } else {
        toast.error(result.error || "Erro ao adicionar tamanho");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <button onClick={() => setOpen(true)} className="inline-block">
          {trigger}
        </button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          title="Adicionar novo tamanho"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Tamanho
        </Button>
      )}

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Tamanho</DialogTitle>
          <DialogDescription>
            Crie um novo tamanho customizado para sua empresa. Você poderá usá-lo em pedidos e orçamentos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="size-label">Rótulo (exibição) *</Label>
            <Input
              id="size-label"
              placeholder="Ex: 37, PP Especial, Tamanho Único..."
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={submitting || isLoading}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Como será exibido na lista de tamanhos
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="size-value">Valor (código interno) *</Label>
            <Input
              id="size-value"
              placeholder="Ex: 37, PP_ESP, UNICO..."
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              disabled={submitting || isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Será convertido para maiúsculas automaticamente
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Exemplo:</strong> Para uma camisa social de colarinho 40, você pode usar:
              <br />
              Rótulo: "40" | Valor: "40"
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={submitting || isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || isLoading || !label.trim() || !value.trim()}
            className="bg-primary hover:bg-primary/90"
          >
            {submitting || isLoading ? "Adicionando..." : "Adicionar Tamanho"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
